import { lastPeriodOfYear } from "utils/getIsoPeriodStart";
import { periodStates } from "./erPeriodStates";

// The fill class per display state. The current and open periods share the open tint, and a
// not-counted column carries no tint.
const stateFillClasses = {
    [periodStates.current]: "er-kpi-col-open",
    [periodStates.open]: "er-kpi-col-open",
    [periodStates.alert]: "er-kpi-col-alert",
    [periodStates.counting]: "er-kpi-col-counting"
};

// Period ordinal used to compare a period against the counting-window bounds.
function periodOrdinal(isoYear, isoPeriod) {
    return isoYear * lastPeriodOfYear + isoPeriod;
}

function isAtOrAfter(period, bound) {
    return periodOrdinal(period.isoYear, period.isoPeriod) >= periodOrdinal(bound.isoYear, bound.isoPeriod);
}

function isAtOrBefore(period, bound) {
    return periodOrdinal(period.isoYear, period.isoPeriod) <= periodOrdinal(bound.isoYear, bound.isoPeriod);
}

// The ordinals an alert was raised in, taken from the response's alert rows. The period's own
// hasTrigger flag is NOT the alert flag — it is set for any period holding a breached KPI cell, which
// is most of them — so the alert shading has to come from the alerts themselves.
function buildAlertOrdinals(alerts) {
    const ordinals = new Set();

    if (alerts == null) {
        return ordinals;
    }

    alerts.forEach((alert) => {
        ordinals.add(periodOrdinal(alert.isoYear, alert.isoPeriod));
    });

    return ordinals;
}

// Resolves a period's display state. An open period wins over an alert: alerts land at the open
// anchors, and a trigger in a period still accumulating can be rectified before the period closes.
// Current is gated on isOpen as well as isCurrent so the label can never name a column the fill does
// not tint as open.
function resolveState(period, countingWindow, alertOrdinals) {
    if (period.isOpen === true) {
        return period.isCurrent === true ? periodStates.current : periodStates.open;
    }

    if (alertOrdinals.has(periodOrdinal(period.isoYear, period.isoPeriod))) {
        return periodStates.alert;
    }

    return isInWindow(period, countingWindow) ? periodStates.counting : periodStates.notCounted;
}

// The display state of every window period, in the order given: 'current', 'open', 'alert',
// 'counting' or 'notCounted'. The column fill is derived from the same state, so a header label and
// its column tint always agree.
export function buildPeriodDisplayStates(periods, countingWindow, alerts) {
    const alertOrdinals = buildAlertOrdinals(alerts);
    return periods.map((period) => resolveState(period, countingWindow, alertOrdinals));
}

// Whether a period falls inside the counting window, which decides the counting fill.
function isInWindow(period, countingWindow) {
    if (countingWindow == null || period.isBeforeFloor === true) {
        return false;
    }

    return isAtOrAfter(period, countingWindow.windowStart) && isAtOrBefore(period, countingWindow.windowEnd);
}

function cellKeyByKpiKey(isoYear, isoPeriod, kpiKey) {
    return `${isoYear}-${isoPeriod}-key-${kpiKey}`;
}

function cellKeyByLabel(isoYear, isoPeriod, kpiLabel) {
    return `${isoYear}-${isoPeriod}-label-${kpiLabel}`;
}

// The grid cells an alert went to the DVSA with: the breaches that caused it plus everything reported
// alongside them. Either list may be absent while the API is mid-deploy, and a breach without a kpiKey
// is matched on its label so an older response still highlights the cells that raised the alert.
export function buildAlertCellKeys(alert) {
    const keys = new Set();

    if (alert == null) {
        return keys;
    }

    const cells = [...(alert.breaches ?? []), ...(alert.alsoReported ?? [])];
    cells.forEach((cell) => {
        if (cell.kpiKey != null) {
            keys.add(cellKeyByKpiKey(cell.isoYear, cell.isoPeriod, cell.kpiKey));
        } else {
            keys.add(cellKeyByLabel(cell.isoYear, cell.isoPeriod, cell.kpiLabel));
        }
    });

    return keys;
}

// Whether the grid cell for a KPI or count row in a period is one of an alert's cells.
export function isAlertCell(alertCellKeys, period, row) {
    return alertCellKeys.has(cellKeyByKpiKey(period.isoYear, period.isoPeriod, row.key))
        || alertCellKeys.has(cellKeyByLabel(period.isoYear, period.isoPeriod, row.label));
}

// Builds the ListingTable columnClasses array for the KPI grid: one entry per column, each period column
// filled from its own display state, and the label column left unshaded.
export function buildKpiColumnClasses(periods, countingWindow, alerts) {
    const states = buildPeriodDisplayStates(periods, countingWindow, alerts);

    return ["", ...states.map((state) => stateFillClasses[state] ?? "")];
}

// True when at least one period in the window carries the marker, so the grid knows to reserve the
// slot on every column header.
export function hasCappedEndedPeriod(periods) {
    return periods.some(isCappedEndedPeriod);
}

// True when the marker should be drawn on a period's column header. The current period is the only
// exclusion — its day and fleet counts are still accumulating, so the flag is not yet meaningful
// there. Every earlier period has finished accumulating, including the previous one, which is open
// only while its figures are reconciled.
export function isCappedEndedPeriod(period) {
    return period.isSmallOperatorCapped === true && period.isCurrent !== true;
}
